import React, { useCallback, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import PersonAvatar from "@/components/PersonAvatar";
import { Camera, Upload, Trash2, Loader2, Crop as CropIcon, RotateCw, ZoomIn, ZoomOut, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

// Utility — render a cropped square from the source image + crop area
async function getCroppedBlob(imageSrc, pixelCrop, rotation = 0, mime = "image/webp", quality = 0.9) {
  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = imageSrc;
  });
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  // Rotation
  const rad = (rotation * Math.PI) / 180;
  if (rotation) {
    const bigCanvas = document.createElement("canvas");
    const size = Math.max(image.width, image.height) * 2;
    bigCanvas.width = size;
    bigCanvas.height = size;
    const bctx = bigCanvas.getContext("2d");
    bctx.translate(size / 2, size / 2);
    bctx.rotate(rad);
    bctx.translate(-image.width / 2, -image.height / 2);
    bctx.drawImage(image, 0, 0);
    ctx.drawImage(
      bigCanvas,
      size / 2 - image.width / 2 + pixelCrop.x,
      size / 2 - image.height / 2 + pixelCrop.y,
      pixelCrop.width, pixelCrop.height, 0, 0, pixelCrop.width, pixelCrop.height,
    );
  } else {
    ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, pixelCrop.width, pixelCrop.height);
  }
  return new Promise((resolve) => canvas.toBlob(resolve, mime, quality));
}

/**
 * PhotoUploader — Upload, Take Photo, Crop, Rotate, Remove. Used for staff & clients.
 */
export default function PhotoUploader({ kind, targetId, person, onUpdated, size = "xl", authed = false }) {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pixelCrop, setPixelCrop] = useState(null);
  const hasPhoto = !!(person?.photo_url || person?.photo_thumb_url);

  const onCropComplete = useCallback((_area, areaPixels) => setPixelCrop(areaPixels), []);

  const openCrop = (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      return toast.error("Use JPEG, PNG or WEBP.");
    }
    if (file.size > 5 * 1024 * 1024) return toast.error("Photo must be 5MB or less.");
    if (!targetId) return toast.error("Save the record first, then add a photo.");
    const reader = new FileReader();
    reader.onload = (e) => {
      setImageSrc(e.target.result);
      setZoom(1); setRotation(0); setCrop({ x: 0, y: 0 });
      setCropOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const applyCrop = async () => {
    if (!imageSrc || !pixelCrop) return;
    setUploading(true);
    try {
      const blob = await getCroppedBlob(imageSrc, pixelCrop, rotation);
      if (!blob) throw new Error("Crop failed");
      const fd = new FormData();
      fd.append("file", blob, "photo.webp");
      const r = await api.post(`/photos/${kind}/${targetId}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Profile photo updated");
      onUpdated?.(r.data);
      setCropOpen(false);
      setImageSrc(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
      if (cameraRef.current) cameraRef.current.value = "";
    }
  };

  const remove = async () => {
    if (!targetId || !hasPhoto) return;
    if (!window.confirm("Remove profile photo?")) return;
    setUploading(true);
    try {
      await api.delete(`/photos/${kind}/${targetId}`);
      toast.success("Profile photo removed");
      onUpdated?.({ photo_id: null, photo_url: null, photo_tile_url: null, photo_thumb_url: null });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-4" data-testid={`photo-uploader-${kind}`}>
        <div className="relative">
          <PersonAvatar person={person} size={size} authed={authed} testid={`photo-preview-${kind}`} />
          {uploading && (
            <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center">
              <Loader2 className="w-5 h-5 text-white animate-spin" />
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5 items-center">
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid={`photo-file-${kind}`} onChange={(e) => openCrop(e.target.files?.[0])} />
          <input ref={cameraRef} type="file" accept="image/*" capture="user" className="hidden" data-testid={`photo-camera-${kind}`} onChange={(e) => openCrop(e.target.files?.[0])} />
          <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`photo-upload-${kind}`}>
            <Upload className="w-3.5 h-3.5 mr-1.5" /> {hasPhoto ? "Replace" : "Upload"}
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => cameraRef.current?.click()} disabled={uploading} data-testid={`photo-take-${kind}`} className="md:hidden">
            <Camera className="w-3.5 h-3.5 mr-1.5" /> Take
          </Button>
          {hasPhoto && (
            <Button type="button" variant="outline" size="sm" onClick={remove} disabled={uploading} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`photo-remove-${kind}`} title="Remove photo">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
          <div className="text-[11px] text-slate-500 basis-full">JPEG, PNG or WEBP · up to 5MB · frame &amp; zoom before saving</div>
        </div>
      </div>

      <Dialog open={cropOpen} onOpenChange={(v) => { if (!uploading) { setCropOpen(v); if (!v) setImageSrc(null); } }}>
        <DialogContent className="max-w-lg bg-white" data-testid={`crop-dialog-${kind}`}>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><CropIcon className="w-4 h-4" /> Frame your photo</DialogTitle>
          </DialogHeader>
          <div className="relative w-full h-72 bg-slate-900 rounded overflow-hidden" data-testid={`crop-canvas-${kind}`}>
            {imageSrc && (
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onRotationChange={setRotation}
                onCropComplete={onCropComplete}
              />
            )}
          </div>
          <div className="grid grid-cols-2 gap-3 pt-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1"><ZoomIn className="w-3 h-3" /> Zoom</label>
              <input type="range" min="1" max="3" step="0.05" value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="w-full accent-sky-700" data-testid={`crop-zoom-${kind}`} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1"><RotateCw className="w-3 h-3" /> Rotate</label>
              <input type="range" min="0" max="360" step="90" value={rotation} onChange={(e) => setRotation(Number(e.target.value))} className="w-full accent-sky-700" data-testid={`crop-rotate-${kind}`} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCropOpen(false)} disabled={uploading} data-testid={`crop-cancel-${kind}`}>
              <X className="w-3.5 h-3.5 mr-1.5" /> Cancel
            </Button>
            <Button type="button" onClick={applyCrop} disabled={uploading || !pixelCrop} className="bg-sky-700 hover:bg-sky-800" data-testid={`crop-apply-${kind}`}>
              {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <CropIcon className="w-3.5 h-3.5 mr-1.5" />}
              Save Photo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
